import { PaymentGuidePage } from '~/features/taxdb/payment-guide-page'
import { DevModeProvider } from '@st/react-util/dev-mode'
import { Organization } from '@st/sdk'
import { ArchiveIcon, Button, FullPageCard, LockClosedIcon } from '@st/theme'
import { clsx } from 'clsx'
import { ReactNode, Suspense } from 'react'
import { match, P } from '@st/pattern'
import { FEATURE_FLAGS } from '~/env'
import { useCurrentUser, useRoute, useSetRoute } from '~/features/app-hooks'
import { RequireLogin } from '~/features/auth/require-login'
import { authModule, logout } from '~/features/auth/auth-module'
import { OrganizationBalancePage } from '~/features/billing/st-organization-balance-page'
import { OrganizationLoggedOutPlansPage } from '~/features/billing/st-organization-plans-page'
import { ClientPacketsPage } from '~/features/client-packets'
import { OneHourCoursePage } from '~/features/courses/one-hour-course'
import {
  OrganizationBrandingPage,
  OrganizationLoggedOutHeader,
  OrganizationLoginPage
} from '~/features/organizations'
import {
  organizationModule,
  type OrganizationState
} from '~/features/organizations/organization-module'
import { loginRoute, routes } from '~/features/routes'
import { CreateFolderImportPage, RunFolderImportPage } from '~/features/folder-import'
import { MagicLinksPage } from '~/features/magic-links'
import { STFolderListPage } from '~/features/st-folder-list'
import { STFolderViewer } from '~/features/st-folder-viewer/st-folder-viewer'
import { FolderOrganizerSettingsDialog } from '~/features/st-folder-viewer/folder-organizer-settings-dialog'
import { STSendClientOrganizerModal } from '~/features/st-folder-viewer/st-send-client-organizer-modal'
import { OrganizationPlansPage } from '~/features/st-organizations'
import { OrganizationAccountSettingsPage } from '~/features/st-organizations/st-organization-account-settings-page'
import { ClientOrganizerSettingsPage } from '~/slices/client-organizer-settings/pages/settings'
import { OrganizationDesktopAgentPage } from '~/features/st-organizations/st-organization-desktop-agent-page'
import { STOrganizationMessageTemplatesPage } from '~/features/st-organizations/st-organization-message-templates-page'
import { stOrganizationModule } from '~/features/st-organizations/st-organization-module'
import { OrganizationIntegrationsPage } from '~/features/st-organizations/st-organizations-integrations-page'
import { snackbarModule } from '~/features/snackbar/snackbar-module'
import { STQuestionnaire } from '~/features/st-questionnaire'
import { useProcess, useSelect } from '~/store'
import { FullScreenNoticePage } from '~/theme'
import { ConnectAppPage } from '../st-organizations/connect-app-page'
import { OrganizationFolderTagsSettingsPage } from '../st-organizations/st-organization-folder-tags-settings-page'
import { OrganizationHeader, StanfordTaxHeader } from './pages/organization-login-page'
import { OrganizationRouteChangedPage } from './pages/organization-route-changed-page'
import { OrganizationSignUpPage } from './pages/organization-sign-up-page'
import { OrganizationTeamInvitationPage } from './pages/organization-team-invitation-page'
import { OrganizationTeamPage } from './pages/organization-team-page'
import { PageNotFound } from './pages/page-not-found'

/**
 * The app that accountants use to personalize their organization,
 * view the submissions of their clients, manage their billing, etc.
 */
export function OrganizationApp({ devMode }: { devMode: boolean }) {
  return (
    <DevModeProvider value={devMode}>
      <SnackbarHost>
        <OrganizationRoutes />
      </SnackbarHost>
    </DevModeProvider>
  )
}

function OrganizationRoutes() {
  const setRoute = useSetRoute()
  const route = useRoute()

  switch (route?.name) {
    case 'payment_guide':
      return <PaymentGuidePage />
    case '/':
    case 'login':
      return <OrganizationLoginPage header={<StanfordTaxHeader />} />
    case 'logged_out_pricing':
      return <OrganizationLoggedOutPlansPage />
    case 'logged_in_pricing':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <OrganizationPlansPage />
        </OrganizationContainerRequiresLogin>
      )
    case 'one_hour_course':
      return (
        <RequireLogin loginScreen={<OrganizationLoginPage header={<StanfordTaxHeader />} />}>
          <Suspense fallback={null}>
            <OneHourCoursePage />
          </Suspense>
        </RequireLogin>
      )
    case 'sign_up':
      return <OrganizationSignUpPage header={<OrganizationLoggedOutHeader />} flow={route.flow} />
    case 'folder_list':
    case 'folder_list_send_client_organizers':
    case 'folder_list_organizer_reminders':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <STFolderListPage />
        </OrganizationContainerRequiresLogin>
      )
    case 'create_import':
    case 'create_import_new_client':
    case 'create_import_new_client_method':
    case 'create_import_returning_client':
    case 'create_import_tax_app':
    case 'create_import_tax_year':
    case 'client_packets_upload_backup':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <CreateFolderImportPage onClose={() => setRoute({ name: 'folder_list' })} />
        </OrganizationContainerRequiresLogin>
      )
    case 'magic_links':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <MagicLinksPage onClose={() => setRoute({ name: 'folder_list' })} />
        </OrganizationContainerRequiresLogin>
      )
    case 'run_import':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <RunFolderImportPage onClose={() => setRoute({ name: 'folder_list' })} />
        </OrganizationContainerRequiresLogin>
      )
    case 'client_packets_export':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <ClientPacketsPage
            flow={route.flow ?? 'personalized-checklists'}
            onClose={() => setRoute({ name: 'folder_list' })}
          />
        </OrganizationContainerRequiresLogin>
      )
    case 'branding':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <OrganizationBrandingPage />
        </OrganizationContainerRequiresLogin>
      )
    case 'team':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <OrganizationTeamPage />
        </OrganizationContainerRequiresLogin>
      )
    case 'team_invitation':
      return <OrganizationTeamInvitationPage />
    case 'organization_folder':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <STFolderViewer folderId={route.folderId} />
        </OrganizationContainerRequiresLogin>
      )
    case 'organization_folder_debug':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <STFolderViewer folderId={route.folderId} debug={true} />
        </OrganizationContainerRequiresLogin>
      )
    case 'organization_preview_questionnaire':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <STQuestionnaire preview={true} folderId={route.folderId} />
        </OrganizationContainerRequiresLogin>
      )
    case 'account':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <OrganizationAccountSettingsPage />
        </OrganizationContainerRequiresLogin>
      )
    case 'manage_organization_tags':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <OrganizationFolderTagsSettingsPage />
        </OrganizationContainerRequiresLogin>
      )
    case 'manage_organization_message_templates':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <STOrganizationMessageTemplatesPage />
        </OrganizationContainerRequiresLogin>
      )
    case 'client_organizer_settings':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <ClientOrganizerSettingsPage />
        </OrganizationContainerRequiresLogin>
      )
    case 'organization_balance':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <OrganizationBalancePage />
        </OrganizationContainerRequiresLogin>
      )
    case 'manage_organization_integrations':
      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <OrganizationIntegrationsPage />
        </OrganizationContainerRequiresLogin>
      )
    case 'desktop_agent_settings':
      if (!FEATURE_FLAGS.pushTaxReturn) {
        return <PageNotFound />
      }

      return (
        <OrganizationContainerRequiresLogin organizationSlug={route.organizationSlug}>
          <OrganizationDesktopAgentPage />
        </OrganizationContainerRequiresLogin>
      )
    case 'connect_app':
      return <ConnectAppPage />
    case 'organization_folder_legacy':
      // Route normalization is handled centrally in `routeModule` (Elm-style input).
      return null
    case 'client_folder_legacy':
      // Route normalization is handled centrally in `routeModule` (Elm-style input).
      return null
    case 'submission_list_legacy':
      return (
        <OrganizationRouteChangedPage
          newRoute={{ name: 'folder_list', organizationSlug: route.organizationSlug }}
        />
      )
    case 'workpaper_list_legacy':
      return (
        <OrganizationRouteChangedPage
          newRoute={{ name: 'folder_list', organizationSlug: route.organizationSlug }}
        />
      )

    default:
      return <PageNotFound />
  }
}

function SnackbarHost({ children }: { children: ReactNode }) {
  const state = useSelect('snackbar', (s) => s)

  return (
    <>
      {match(state)
        .with({ status: 'idle' }, () => null)
        .with({ status: 'showing' }, (state) => (
          <div
            className={clsx(
              'pointer-events-auto fixed bottom-6 left-6 z-50 flex touch-auto select-none flex-row items-center justify-start rounded p-3 px-3 py-1.5 text-base text-white',
              {
                'bg-red-600': state.variant == 'error',
                'bg-gray-800': state.variant != 'error'
              }
            )}
          >
            {state.message}
          </div>
        ))
        .exhaustive()}
      {children}
    </>
  )
}

function OrganizationContainerRequiresLogin({
  children,
  organizationSlug
}: {
  children: ReactNode
  organizationSlug: string
}) {
  const organizationProcess = useProcess('organization')
  const organizationState = useSelect('organization', (state) => state)
  const organization = loadedOrganization(organizationState)

  if (organizationState.status == 'loadFailed') {
    return (
      <OrganizationLoadFailedNotice
        onRetry={() => organizationProcess.send({ type: 'retryOrganizationRequested' })}
      />
    )
  }

  if (organizationState.status == 'unavailable') {
    return <PageNotFound />
  }

  if (organization?.status == 'deactivated') {
    return <AccountDeactivatedNotice organization={organization} />
  }

  return (
    <RequireLogin
      loginScreen={
        <OrganizationLoginPage header={<OrganizationHeader organization={organization} />} />
      }
      loadingScreen={<></>}
    >
      {organization ? (
        <LoadOrganizationState organization={organization}>{children}</LoadOrganizationState>
      ) : null}
    </RequireLogin>
  )
}

function loadedOrganization(state: OrganizationState) {
  if (state.status == 'loaded') return state.organization
  return undefined
}

function OrganizationLoadFailedNotice({ onRetry }: { onRetry: () => void }) {
  return (
    <FullPageCard>
      <div className="flex flex-col items-center gap-4 text-center">
        <div>We could not load this organization. Please check your connection and try again.</div>
        <Button variant="primary" onClick={onRetry}>
          Retry
        </Button>
      </div>
    </FullPageCard>
  )
}

function AccountDeactivatedNotice({ organization }: { organization: Organization }) {
  const auth = useProcess('auth')
  return (
    <FullScreenNoticePage title="Account Deactivated" icon={<ArchiveIcon className="size-10" />}>
      <p className="max-w-2xl text-center">
        This account {organization.name} ({organization.slug}) is deactivated.
      </p>
      <p className="max-w-2xl text-center">
        If you would like to reactivate your account and restore your data, please contact us at
        support@stanfordtax.com.
      </p>

      <Button
        variant="default"
        className="mt-4"
        onClick={() => auth.send(logout({ type: 'path', pathname: loginRoute({}) }))}
      >
        Logout
      </Button>
    </FullScreenNoticePage>
  )
}

function LoadOrganizationState({
  organization,
  children
}: {
  organization: Organization
  children: ReactNode
}) {
  const auth = useProcess('auth')
  const stOrganization = useProcess('stOrganization')
  const user = useCurrentUser()

  const state = useSelect('stOrganization', (s) => s)

  return match(state)
    .with({ status: 'idle' }, () => null)
    .with({ status: 'loadingOrganization' }, () => null)
    .with({ status: 'failed', stage: 'resolveOrganization' }, () => null)
    .with({ status: 'failed', stage: 'loadOrganization' }, () => (
      <OrganizationLoadFailedNotice
        onRetry={() => stOrganization.send({ type: 'retryOrganizationStateRequested' })}
      />
    ))
    .with({ status: 'loaded' }, () => (
      <>
        {children}
        <STSendClientOrganizerModal />
        <FolderOrganizerSettingsDialog />
      </>
    ))
    .with({ status: 'unauthorized' }, () => (
      <FullPageCard>
        <div className="flex flex-col items-center gap-4">
          <LockClosedIcon className="size-10" />
          <div className="text-center">
            You are not authorized to access <br /> {organization.name}.
          </div>
          {user ? <div className="text-center text-base">Logged in as {user.email}</div> : null}

          <Button variant="default" onClick={() => auth.send({ type: 'logout' })}>
            Logout
          </Button>
        </div>
      </FullPageCard>
    ))
    .exhaustive()
}

// Redirect components are intentionally avoided (anti-Elm). Canonicalize routes in `routeModule`.
